import { toggleIn } from "./hikvisionControl";
import { OptionCard, SectionHeader, StepBody, StepHeader, StepNav } from "./HikvisionWizardParts";

const ZONE_STATUS = {
    linking: { label: "Linking...", background: "#e3f2fd", color: "#1565c0", border: "#90caf9" },
    linked: { label: "Linked", background: "#e8f5e9", color: "#2e7d32", border: "#a5d6a7" },
    failed: { label: "Failed", background: "#fdecea", color: "#c0392b", border: "#f5b7b1" },
};

const ZoneStatusPill = ({ status }) => {
    const style = ZONE_STATUS[status];
    return (
        <span style={{
            fontSize: "11px", fontWeight: 600, padding: "2px 8px", borderRadius: "10px", flexShrink: 0,
            background: style.background, color: style.color, border: `1px solid ${style.border}`,
        }}>
            {style.label}
        </span>
    );
};

// Zones already linked during this wizard run (zones mode only)
const LinkedZones = ({ zones }) => (
    <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginBottom: "4px" }}>
        <SectionHeader label={`Linked zones (${zones.length})`} />
        {zones.map(zone => (
            <div key={zone.id} style={{
                display: "flex", alignItems: "center", gap: "10px", padding: "8px 12px",
                border: "1px solid #e0e0e0", borderRadius: "8px", fontSize: "13px",
            }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontWeight: 600, color: "#2d2d2d" }}>{zone.pinName}</p>
                    <p style={{ margin: "2px 0 0 0", color: "#9e9e9e", fontSize: "12px", wordBreak: "break-word" }}>
                        {zone.ruleNames.join(", ")}
                        {zone.channelCount > 0 && ` · ${zone.channelCount} camera${zone.channelCount === 1 ? "" : "s"}`}
                        {zone.status === "failed" && zone.failReason && ` · ${zone.failReason}`}
                    </p>
                </div>
                <ZoneStatusPill status={zone.status} />
            </div>
        ))}
    </div>
);

// Picks the Hikvision rules (login events) to control, listed under their event group.
// The cameras they apply to are chosen afterwards (HikvisionStepCameras).
// In zones mode this is the start of each zone - linkedZones/onFinish let the user add another zone or finish.
const HikvisionStepRules = ({ events, selectedEvents, setSelectedEvents, onNext, onBack, isLastStep, linkedZones, onFinish }) => {
    const groupLabels = [...new Set(events.map(event => event.groupLabel))];
    const zonesMode = !!onFinish;
    const zonesStillLinking = linkedZones?.some(zone => zone.status === "linking");

    return (
        <>
            <StepHeader
                title={zonesMode ? "Which rules are in use for this zone?" : "Which Hikvision rules would you like to control?"}
                subtitle={zonesMode
                    ? "Each zone links its rules and cameras to one site output. Add as many zones as you need, then finish."
                    : "Selected rules will be armed and disarmed by the site output"}
            />
            <StepBody>
                {linkedZones?.length > 0 && <LinkedZones zones={linkedZones} />}
                {groupLabels.length === 0 && (
                    <p style={{ color: "var(--text-label)", fontSize: "14px", textAlign: "center", marginTop: "2rem" }}>
                        No rules were found on this Hikvision.
                    </p>
                )}
                {groupLabels.map(groupLabel => (
                    <div key={groupLabel} style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        <SectionHeader label={groupLabel} />
                        {events
                            .filter(event => event.groupLabel === groupLabel)
                            .map((event, index) => (
                                <OptionCard
                                    key={`${event.group}-${event.eventType}-${index}`}
                                    title={event.displayName}
                                    selected={selectedEvents.includes(event)}
                                    onClick={() => setSelectedEvents(prev => toggleIn(prev, event))}
                                />
                            ))}
                    </div>
                ))}
            </StepBody>
            <StepNav
                onBack={onBack}
                onNext={onNext}
                nextDisabled={selectedEvents.length === 0}
                nextLabel={isLastStep ? "Link Hikvision" : "Next"}
                nextIcon={isLastStep ? "pi pi-link" : "pi pi-arrow-right"}
                secondaryAction={zonesMode && {
                    label: "Finish",
                    icon: "pi pi-check",
                    onClick: onFinish,
                    // can't finish until at least one zone is linked and none are still in flight
                    disabled: !linkedZones.some(zone => zone.status === "linked") || zonesStillLinking,
                }}
            />
        </>
    );
};

export default HikvisionStepRules;
